import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Alert,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Avatar,
  Divider,
  LinearProgress
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const { company, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get('/subscriptions/current');
      setSubscription(response.data.subscription);
    } catch (err) {
      console.error('Failed to load subscription', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'trial':
        return 'info';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <BusinessIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Code Interview Platform
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {company?.email}
            </Typography>
            <IconButton 
              color="inherit" 
              onClick={handleMenuOpen}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { mt: 1, minWidth: 200 }
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">{company?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#1a237e' }}>
            Welcome back, {company?.name}! 👋
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your interview platform today
          </Typography>
        </Box>

        {company?.subscription_status !== 'active' && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
            action={
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => navigate('/subscription-plans')}
              >
                Choose a Plan
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              You don't have an active subscription. Subscribe now to start conducting interviews!
            </Typography>
          </Alert>
        )}

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Subscription Status
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {company?.subscription_status?.toUpperCase()}
                </Typography>
                <Chip
                  label={company?.subscription_status === 'active' ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <SubscriptionsIcon 
                sx={{ 
                  position: 'absolute', 
                  right: -10, 
                  bottom: -10, 
                  fontSize: 100, 
                  opacity: 0.2 
                }} 
              />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Current Plan
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {subscription?.plan?.name || 'No Plan'}
                </Typography>
                {subscription?.plan && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    ${subscription.plan.price}/{subscription.plan.billing_cycle === 'yearly' ? 'yr' : 'mo'}
                  </Typography>
                )}
              </Box>
              <TrendingUpIcon 
                sx={{ 
                  position: 'absolute', 
                  right: -10, 
                  bottom: -10, 
                  fontSize: 100, 
                  opacity: 0.2 
                }} 
              />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Total Sessions
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  0
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  All time
                </Typography>
              </Box>
              <HistoryIcon 
                sx={{ 
                  position: 'absolute', 
                  right: -10, 
                  bottom: -10, 
                  fontSize: 100, 
                  opacity: 0.2 
                }} 
              />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  {company?.subscription_ends_at ? 'Expires On' : 'Member Since'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {company?.subscription_ends_at 
                    ? new Date(company.subscription_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Today'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {company?.subscription_ends_at ? 'Renewal date' : 'Welcome aboard!'}
                </Typography>
              </Box>
              <CalendarTodayIcon 
                sx={{ 
                  position: 'absolute', 
                  right: -10, 
                  bottom: -10, 
                  fontSize: 100, 
                  opacity: 0.2 
                }} 
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Company Information Card */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: '#1976d2',
                    mr: 2
                  }}
                >
                  <BusinessIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Company Profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your organization details
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Company Name
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {company?.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {company?.email}
                  </Typography>
                </Box>
                {company?.phone && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {company.phone}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate('/settings')}
                  sx={{ mt: 2 }}
                >
                  Edit Profile
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: company?.subscription_status === 'active' ? 'pointer' : 'not-allowed',
                      opacity: company?.subscription_status === 'active' ? 1 : 0.6,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: company?.subscription_status === 'active' ? 'translateY(-4px)' : 'none',
                        boxShadow: company?.subscription_status === 'active' ? 4 : 1,
                      },
                      border: '1px solid #e0e0e0'
                    }}
                    onClick={() => company?.subscription_status === 'active' && navigate('/editor')}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                          <PlayArrowIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Start Interview
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Begin a new coding interview session with real-time collaboration
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: company?.subscription_status === 'active' ? 'pointer' : 'not-allowed',
                      opacity: company?.subscription_status === 'active' ? 1 : 0.6,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: company?.subscription_status === 'active' ? 'translateY(-4px)' : 'none',
                        boxShadow: company?.subscription_status === 'active' ? 4 : 1,
                      },
                      border: '1px solid #e0e0e0'
                    }}
                    onClick={() => company?.subscription_status === 'active' && navigate('/sessions')}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#f5576c', mr: 2 }}>
                          <HistoryIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          View Sessions
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Review past interview sessions and candidate performance
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: company?.subscription_status === 'active' ? 'pointer' : 'not-allowed',
                      opacity: company?.subscription_status === 'active' ? 1 : 0.6,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: company?.subscription_status === 'active' ? 'translateY(-4px)' : 'none',
                        boxShadow: company?.subscription_status === 'active' ? 4 : 1,
                      },
                      border: '1px solid #e0e0e0'
                    }}
                    onClick={() => company?.subscription_status === 'active' && navigate('/interviewers')}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#43e97b', mr: 2 }}>
                          <PeopleIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Manage Interviewers
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Add, edit, or remove interviewer accounts and permissions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                      border: '1px solid #e0e0e0'
                    }}
                    onClick={() => navigate('/settings')}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#764ba2', mr: 2 }}>
                          <SettingsIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Settings
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Configure your account, notifications, and preferences
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            {/* Subscription Info */}
            {subscription && subscription.plan && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Subscription Details
                  </Typography>
                  <Chip
                    label={company?.subscription_status?.toUpperCase()}
                    color={getStatusColor(company?.subscription_status)}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Plan Name
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                      {subscription.plan.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Billing
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                      ${subscription.plan.price}/{subscription.plan.billing_cycle === 'yearly' ? 'year' : 'month'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {company?.subscription_ends_at ? 'Renewal Date' : 'Status'}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                      {company?.subscription_ends_at 
                        ? new Date(company.subscription_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : 'Active'}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/subscription-plans')}
                    sx={{ flex: 1 }}
                  >
                    {company?.subscription_status === 'active' ? 'Change Plan' : 'Subscribe Now'}
                  </Button>
                </Box>
              </Paper>
            )}

            {!subscription?.plan && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                <SubscriptionsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  No Active Subscription
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Subscribe to a plan to start using all features
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/subscription-plans')}
                >
                  View Plans
                </Button>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

